import { FormikTouched } from 'formik';
import React, { useMemo } from 'react';

import {
  Autocomplete,
  Checkbox,
  DatePicker,
  Radio,
} from '@dogtainers/dgt-blocks/src/components/form';
import { useIsMobile } from '@dogtainers/dgt-blocks/src/hooks/useBreakpoints';
import { Grid, Typography, makeStyles } from '@material-ui/core';
import { alpha } from '@material-ui/core/styles/colorManipulator';
import IconInfo from '@material-ui/icons/InfoRounded';

import { defaultLocale } from '../../../utils/language';
import {
  handleAutoFocus,
  handleFocusField,
  isAULocale,
  isGBLocale,
} from '../../utils';
import { DestinationFields } from '../components/DestinationFields';
import { usePickupAndDeliveryAddress } from '../hooks/usePickupAndDeliveryAddress';
import { PageFormProps } from '../quote.form';
import { BookingQuote, QuoteFormPartial } from '../quote.types';
import { isHiddenField } from '../quote.validation';

export const useStyles = makeStyles((theme) => ({
  darwinNoticeBox: {
    backgroundColor: alpha(theme.palette.primary.light, 0.1),
    padding: '1rem',
    borderRadius: '0.5rem',
    border: `1px solid ${theme.palette.primary.main}`,
  },
  iconLabel: {
    color: theme.palette.primary.main,
    verticalAlign: 'text-bottom',
    fontSize: '1.2rem',
    marginRight: '0.5rem',
  },
}));

export const QuotePage2: React.FC<PageFormProps> = ({
  values,
  handleChange,
  setFieldValue,
  errors,
  touched,
  handleBlur,
  isErrorsVisible,
  isFieldInFocus,
  children,
}) => {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const {
    arrival,
    departure,
    pickupAddress,
    deliveryAddress,
    pickupAddresses,
    deliveryAddresses,
    handleInputMapChange,
    handleAddressChange,
    generateAutocompleteItems,
  } = usePickupAndDeliveryAddress({
    values,
    handleChange,
    setFieldValue,
  });

  const handleDepartureDateChange = (e: React.ChangeEvent<HTMLElement>) => {
    setFieldValue(
      'options.petAccommodation.start',
      (e.target as HTMLInputElement).value,
    );
    handleChange(e);
  };

  //No postcode for Hong Kong (DOG-588)
  const isHongKongDeparture = useMemo(
    () => values?.departure?.countryId === 'HK' && values?.departure?.isPickup,
    [values?.departure],
  );

  const isHongKongArrival = useMemo(
    () => values?.arrival?.countryId === 'HK' && values?.arrival?.isDelivery,
    [values?.arrival],
  );

  const isFromDarwin = useMemo(
    () => [values?.departure?.airportId].includes('DRW'),
    [values?.arrival, values?.departure],
  );

  return (
    <Grid container justifyContent="center" spacing={4}>
      <Grid item xs={12}>
        <Typography variant="h4">Itinerary</Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="body1">
          Here we need to understand what is the nearest airport for departure
          and arrival.
        </Typography>
      </Grid>
      {/* {isFromDarwin && (
        <Grid item>
          <Typography variant="body1" className={classes.darwinNoticeBox}>
            Due to high demand for departures from <strong>Darwin</strong>, all
            quotes provided are based on current airline scheduling rather than
            availability. Please note that requested travel dates may not be
            guaranteed.
            <br />
            For further assistance, please contact our call centre on 1300 13 52
            52 to speak with a Pet Travel Consultant.
          </Typography>
        </Grid>
      )} */}
      {children}
      <Grid item xs={12}>
        <Grid container spacing={3}>
          <DestinationFields
            values={values}
            errors={errors as QuoteFormPartial}
            handleChange={handleChange}
            setFieldValue={setFieldValue}
            handleBlur={handleBlur}
            touched={touched as FormikTouched<BookingQuote>}
            isErrorsVisible={isErrorsVisible}
            isFieldInFocus={isFieldInFocus}
          />

          <Grid item xs={12} sm={6}>
            <DatePicker
              label="Estimated Date of Travel"
              name="departure.date"
              onChange={handleDepartureDateChange}
              value={departure?.date}
              error={errors?.departure?.date}
              isErrorVisible={isErrorsVisible}
              touched={touched.departure?.date}
              onBlur={handleBlur}
              innerRef={isFieldInFocus?.('departure.date')}
              key={`${!errors?.arrival?.airportId}_departure.date`}
              autoFocus={handleAutoFocus(
                isMobile,
                !arrival?.airportId || errors?.arrival?.airportId,
                departure?.date,
              )}
              onFocus={(e) => handleFocusField(isMobile, e.target)}
            />
            <Grid item>
              <Typography variant="body1" style={{ marginTop: '0.4rem' }}>
                <IconInfo className={classes.iconLabel} />
                Note: Prices may be cheaper for travel on weekdays.
              </Typography>
            </Grid>
          </Grid>

          {!isGBLocale && (
            <Grid item xs={12} sm={6} md={3}>
              <Checkbox
                label="Special Arrangements?"
                menuItems={[
                  {
                    name: 'departure.isPickup',
                    label: 'Pickup',
                    value: !!departure?.isPickup,
                  },
                  {
                    name: 'arrival.isDelivery',
                    label: 'Delivery',
                    value: !!arrival?.isDelivery,
                  },
                ]}
                key={`${!errors?.departure?.date}_departure.isPickup`}
                autoFocus={handleAutoFocus(
                  isMobile,
                  errors?.departure?.date,
                  departure?.isPickup && arrival?.isDelivery,
                )}
                onChange={handleChange}
                onFocus={(e) => handleFocusField(isMobile, e.target)}
              />
            </Grid>
          )}

          {values.quoteType === 'international' &&
            !isHiddenField(defaultLocale, 'arrival.isWithin5Days') && (
              <Grid item xs={12} sm={6} md={3}>
                <Radio
                  name="arrival.isWithin5Days"
                  label="Do you intend to arrive within 5 days of your pet?"
                  value={arrival?.isWithin5Days}
                  error={String(errors?.arrival?.isWithin5Days)}
                  onChange={handleChange}
                  innerRef={isFieldInFocus?.('arrival.isWithin5Days')}
                  onFocus={(e) => handleFocusField(isMobile, e.target)}
                  menuItems={[
                    {
                      label: 'No',
                      value: false,
                    },
                    {
                      label: 'Yes',
                      value: true,
                    },
                  ]}
                />
              </Grid>
            )}

          {!isGBLocale && !!departure?.isPickup && !isHongKongDeparture && (
            <Grid item xs={12} sm={6}>
              <Autocomplete
                menuItems={generateAutocompleteItems(pickupAddresses)}
                placeholder={`Enter ${isAULocale ? 'suburb' : 'address'}`}
                label={`Pickup ${isAULocale ? 'Suburb' : 'Address'}`}
                name="departure.pickupAddress.address"
                onInputChange={(value: string) => {
                  handleInputMapChange(value, 'pickup');
                }}
                onChange={(e) => {
                  handleAddressChange(e, 'departure.pickupAddress');
                }}
                value={pickupAddress?.address || ''}
                error={errors?.departure?.pickupAddress?.address}
                controlledState
                bypassFilterOptions
                touched={touched.departure?.pickupAddress}
                isErrorVisible={isErrorsVisible}
                onBlur={handleBlur}
                innerRef={isFieldInFocus?.('departure.pickupAddress.address')}
                onFocus={(e) => handleFocusField(isMobile, e.target)}
              />
            </Grid>
          )}
          {!isGBLocale && !!arrival?.isDelivery && !isHongKongArrival && (
            <Grid item xs={12} sm={6}>
              <Autocomplete
                menuItems={generateAutocompleteItems(deliveryAddresses)}
                placeholder={`Enter ${isAULocale ? 'suburb' : 'address'}`}
                label={`Delivery ${isAULocale ? 'Suburb' : 'Address'}`}
                name="arrival.deliveryAddress.address"
                onInputChange={(value: string) => {
                  handleInputMapChange(value, 'delivery');
                }}
                onChange={(e) =>
                  handleAddressChange(e, 'arrival.deliveryAddress')
                }
                value={deliveryAddress?.address || ''}
                error={errors?.arrival?.deliveryAddress?.address}
                controlledState
                bypassFilterOptions
                touched={touched.arrival?.deliveryAddress}
                isErrorVisible={isErrorsVisible}
                onBlur={handleBlur}
                innerRef={isFieldInFocus?.('arrival.pickupAddress.address')}
                key={`${!errors?.departure?.pickupAddress
                  ?.address}_arrival.deliveryAddress.address`}
                autoFocus={handleAutoFocus(
                  isMobile,
                  errors?.departure?.pickupAddress?.address,
                  deliveryAddress?.address,
                )}
                onFocus={(e) => handleFocusField(isMobile, e.target)}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};
